import { type ReactNode } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useLanguage } from '../../../../../context/LanguageContext';

const TEXT_BUTTON_SX = { textTransform: 'none' } as const;

export function SelectionHeader({ summary, onOpen }: { summary: string; onOpen: () => void }) {
    const { t } = useLanguage();
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600, minWidth: 0 }}>
                {summary}
            </Typography>
            <Button size="small" variant="outlined" onClick={onOpen} sx={{ ...TEXT_BUTTON_SX, flexShrink: 0 }}>
                {t('pages.clients.queryBuilder.simple.selectionDialog.openButton')}
            </Button>
        </Box>
    );
}

interface SelectionDialogProps {
    open: boolean;
    title: string;
    maxWidth: 'sm' | 'md';
    height: number;
    onCancel: () => void;
    onValidate: () => void;
    fullScreen: boolean;
    children: ReactNode;
}

export function useDialogFullScreen(): boolean {
    const theme = useTheme();
    return useMediaQuery(theme.breakpoints.down('sm'));
}

export function SelectionDialog({ open, title, maxWidth, height, onCancel, onValidate, fullScreen, children }: SelectionDialogProps) {
    const { t } = useLanguage();

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth={maxWidth}
            fullWidth
            fullScreen={fullScreen}
            PaperProps={{ sx: { height: fullScreen ? '100%' : height } }}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {children}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} sx={TEXT_BUTTON_SX}>
                    {t('pages.clients.queryBuilder.simple.selectionDialog.cancel')}
                </Button>
                <Button onClick={onValidate} variant="contained" sx={TEXT_BUTTON_SX}>
                    {t('pages.clients.queryBuilder.simple.selectionDialog.validate')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
